import React, { lazy, Suspense, useRef } from 'react'
import { ConfigProvider, Dropdown } from 'antd'
import cls from 'classnames'
import styled, { css } from 'styled-components'

import useDownloadHandler from 'utils/hooks/useDownloadHandler'

import Popup from 'components/lib/Popup'

import { ChevronDown, Loader } from './Icons'
import StatusDot from './StatusDot'

const OldModal = lazy(() => import('components/lib/modules/OldModal'))

const STYLE_TYPES = ['primary', 'secondary', 'tertiary', 'fourth', 'upgrade', 'danger']

const ICON_SIZE = '16px'

const fillMap = {
  primary: 'var(--white)',
  secondary: 'var(--primary)',
  tertiary: 'var(--primary)',
  fourth: 'var(--primary)',
  upgrade: 'var(--primary)',
  danger: 'var(--negative-color)',
  default: 'var(--white)',
}

export const getButtonStyleBlock = ({
  styleType = 'primary',
  noBackground,
  disabled,
  loader,
  iconColor,
}) => {
  const resolvedStyleType = STYLE_TYPES.includes(styleType) ? styleType : 'primary'

  const bgOrTransparent = (color) => (noBackground ? 'transparent' : color)

  return css`
    border-width: 1px;
    border-style: solid;
    transition:
      background-color 0.2s ease,
      border-color 0.2s ease,
      color 0.2s ease;
    line-height: normal;
    border-radius: var(--round-button-radius);
    border-color: var(--button-${resolvedStyleType}-border-default);
    color: var(--button-${resolvedStyleType}-text-default);
    background-color: ${bgOrTransparent(`var(--button-${resolvedStyleType}-bg-default)`)};
    svg path,
    .left-icon svg path,
    .left-icon svg > path,
    .right-icon svg path,
    .right-icon svg > path {
      fill: ${iconColor || `var(--button-${resolvedStyleType}-text-default)`};
    }

    &:hover,
    &.__hover {
      color: var(--button-${resolvedStyleType}-text-hover);
      border-color: var(--button-${resolvedStyleType}-border-hover);
      background-color: ${
        disabled && noBackground
          ? 'transparent'
          : bgOrTransparent(`var(--button-${resolvedStyleType}-bg-hover)`)
      };
      svg path,
      .left-icon svg path,
      .left-icon svg > path,
      .right-icon svg path,
      .right-icon svg > path {
        fill: ${iconColor || `var(--button-${resolvedStyleType}-text-hover)`};
      }
    }

    &:active,
    &.active {
      color: var(--button-${resolvedStyleType}-text-active);
      border-color: var(--button-${resolvedStyleType}-border-active);
      background-color: ${
        disabled && noBackground
          ? 'transparent'
          : bgOrTransparent(`var(--button-${resolvedStyleType}-bg-active)`)
      };
      svg path,
      .left-icon svg path,
      .left-icon svg > path,
      .right-icon svg path,
      .right-icon svg > path {
        fill: ${iconColor || `var(--button-${resolvedStyleType}-text-active)`};
      }
    }

    &:disabled {
      color: var(--button-${resolvedStyleType}-text-disabled);
      border-color: var(--button-${resolvedStyleType}-border-disabled);
      background-color: ${bgOrTransparent(`var(--button-${resolvedStyleType}-bg-disabled)`)};
      svg path,
      .left-icon svg path,
      .left-icon svg > path,
      .right-icon svg path,
      .right-icon svg > path {
        fill: var(--button-${resolvedStyleType}-text-disabled);
      }
      ${
        styleType === 'upgrade' &&
        !loader &&
        `
          opacity: 0.5;
        `
      }
    }

    &.loader {
      color: var(--button-${resolvedStyleType}-text-loader);
      border-color: var(--button-${resolvedStyleType}-border-loader);
      background-color: var(--button-${resolvedStyleType}-bg-loader);
      svg path,
      .left-icon svg path,
      .left-icon svg > path,
      .right-icon svg path,
      .right-icon svg > path {
        fill: var(--button-${resolvedStyleType}-text-default);
      }
      .left-icon,
      .right-icon {
        svg path,
        svg > path {
          fill: var(--button-${resolvedStyleType}-text-loader);
        }
      }
    }
  `
}

const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${({ noMinWidth }) => (noMinWidth ? 'none;' : '70px;')};
  ${(props) => (props.disabled || props.loader ? '' : 'cursor: pointer;')}
  height: 32px;
  padding: 8px 12px;
  vertical-align: middle;
  outline: none;
  /* Keyboard focus indicator (WCAG 2.4.7). Only shows for keyboard focus, so
     mouse users never see it. Replaces the bare outline:none above. */
  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }
  position: relative;
  white-space: nowrap;
  text-align: center;
  user-select: none;
  .left-icon {
    /* Logical so the icon/label gap mirrors correctly in RTL (no-op in LTR). */
    margin-inline-end: 6px;
  }
  .right-icon {
    margin-inline-start: 6px;
  }

  ${({ iconSameColor }) =>
    iconSameColor &&
    `
    svg,
    svg > path {
      fill: currentColor;
    }
  `}

  .is-button-internal-container {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .button-loader-wrapper {
    border-radius: var(--square-button-radius);
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    ${({ styleType }) => {
      const fill = fillMap[styleType] || fillMap.default

      return `
    svg,
    svg > path {
      fill: ${fill};
    }
  `
    }}
  }

  .button-counter {
    color: var(--button-primary-text-default);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    min-width: 15px;
    min-height: 15px;
    padding: 0 4.5px;
    border-radius: 100px;
    position: absolute;
    top: -4px;
    right: -4px;
    background-color: var(--primary);
  }

  .ant-dropdown-menu {
    color: var(--ds-gray-1);
    margin-top: -4px;
    padding: 8px;
  }

  .ant-dropdown-menu-item {
    color: var(--ds-gray-1);
    padding: 7px 8px;
    border-radius: 4px;

    &:hover {
      background-color: var(--dropdown-hover);
    }
  }

  * {
    ::-webkit-scrollbar {
      width: 4px;
    }
    ::-webkit-scrollbar-thumb {
      -webkit-border-radius: 4px;
      border-radius: 4px;
      background: #e4e4e4;
    }
  }
  ${(props) => getButtonStyleBlock(props)}
`

/**
 * Styled Button component with flexible customization options.
 * @typedef {Object} StyledButtonProps
 * @property {string} id = 'button' - Unique identifier for the button (required).
 * @property {string} [icon] - Optional icon name for legacy icon rendering.
 * @property {string} [text] - Button label text.
 * @property {string} [type] - The type attribute of the button (e.g., 'button', 'submit', 'reset').
 * @property {Object} [style] - Custom inline styles for the button.
 * @property {{ text: string, position: string }} [popup] - Popup content and position.
 * @property {{ text: string }} [confirmationModal] - Configuration for confirmation modal.
 * @property {string} [LeftIcon] - Name of the icon rendered on the left side.
 * @property {string} [RightIcon] - Name of the icon rendered on the right side.
 * @property {Object} [dsIconStyles] - Custom styles applied to icons.
 * @property {boolean} [noBackground] - If true, the button will have a transparent background.
 * @property {boolean} [disabled] - Whether the button is disabled.
 * @property {'primary' | 'secondary' | 'tertiary' | 'fourth' | 'upgrade' | 'danger'} [styleType] - Style variant for the button.
 * @property {{ size: 'small' | 'default', status: keyof typeof STATUS_DOT }} [statusDotProps] - Optional status dot configuration.
 * @property {string} [hoverColor] - Custom background color when hovering.
 * @property {number} [counter] - Optional counter badge displayed on the button (e.g., notifications).
 * @property {boolean} [split] - If true, the button will support a dropdown split mode.
 * @property {string} [as] - The HTML element to render the button as (e.g., 'span', 'div').
 * @property {string} [dataPendoFeature] - Stable Pendo Feature value (`data-pendo-feature` on the button).
 */
const Button = ({
  id = 'button',
  className,
  styleType,
  text,
  type = 'button',
  title,
  style,
  popup,
  loader,
  disabled,
  confirmationModal,
  onClick,
  counter,
  dataPendoFeature,
  LeftIcon,
  RightIcon,
  dsIconStyles,
  href,
  downloadObj,
  downloadIt,
  split,
  options,
  dropdownArgs = {},
  active,
  statusDotProps,
  iconSameColor,
  as = 'button',
}) => {
  const { handleDownloadClick, isLoading } = useDownloadHandler()
  const wrapperRef = useRef(null)

  const isDisabled = disabled || loader || isLoading
  const ccn = cls(
    'is-button typography-subtitle-3',
    className,
    { disabled: isDisabled, active },
    { loader: loader || isLoading }
  )

  const handleOnClick = (e) => {
    if (isDisabled) return

    if (downloadIt || downloadObj) {
      handleDownloadClick({ e, downloadIt, isDisabled, downloadObj, href })
    } else if (typeof onClick === 'function') {
      onClick(e)
    }
  }

  const renderIconLeft = () => {
    if (LeftIcon) {
      return (
        <LeftIcon className="left-icon" width={ICON_SIZE} height={ICON_SIZE} {...dsIconStyles} />
      )
    }

    return null
  }

  const renderIconRight = () => {
    if (split) {
      return (
        <ChevronDown
          className="right-icon"
          width={ICON_SIZE}
          height={ICON_SIZE}
          {...dsIconStyles}
        />
      )
    }

    if (RightIcon) {
      return (
        <RightIcon className="right-icon" width={ICON_SIZE} height={ICON_SIZE} {...dsIconStyles} />
      )
    }

    return null
  }

  const renderButtonContent = () => (
    <div className="is-button-internal-container">
      {statusDotProps?.status && !loader && <StatusDot {...statusDotProps} size="small" />}
      {renderIconLeft()}
      <span className="dib-vac typography-subtitle-3">{text}</span>
      {renderIconRight()}
      {counter > 0 && <div className="button-counter">{counter > 99 ? '99+' : counter}</div>}
    </div>
  )

  const BaseButton = (
    <StyledButton
      id={id}
      style={style}
      className={ccn}
      type={type}
      onClick={handleOnClick}
      title={title}
      disabled={isDisabled}
      styleType={styleType}
      loader={loader || isLoading}
      iconColor={dsIconStyles?.fill}
      iconSameColor={iconSameColor}
      as={as}
      data-pendo-feature={dataPendoFeature}
    >
      {(loader || isLoading) && (
        <div className="button-loader-wrapper">
          <Loader
            style={{ animationDuration: '1000ms' }}
            spin
            fill="var(--white)"
            width="16px"
            height="16px"
          />
        </div>
      )}
      <Popup
        position={popup?.position || 'top'}
        ContentComponent={popup?.text || null}
        style={{ maxWidth: 'unset' }}
        TriggerComponent={renderButtonContent()}
      />
    </StyledButton>
  )

  const ButtonWithOptionalDropdown = split ? (
    <div ref={wrapperRef} style={{ display: 'inline-flex', flexDirection: 'column' }}>
      <ConfigProvider
        theme={{
          token: {
            colorBgElevated: 'var(--color-elevated-solid)',
            colorText: 'var(--color-text)',
            controlItemBgHover: 'var(--color-elevated-mark)',
          },
        }}
      >
        <Dropdown
          id="storybook_select"
          menu={{ items: options }}
          placement="bottomLeft"
          trigger={['click']}
          {...dropdownArgs}
          getPopupContainer={() => wrapperRef.current}
        >
          <div>{BaseButton}</div>
        </Dropdown>
      </ConfigProvider>
    </div>
  ) : (
    BaseButton
  )

  return confirmationModal && !disabled ? (
    <Suspense fallback={null}>
      <OldModal
        trigger={ButtonWithOptionalDropdown}
        size="mini"
        confirm={handleOnClick}
        customConfirmYes="Yes"
        customConfirmNo="No"
      >
        <div className="tac mt">{confirmationModal.text}</div>
      </OldModal>
    </Suspense>
  ) : (
    ButtonWithOptionalDropdown
  )
}

Button.STYLE_TYPES = STYLE_TYPES

export default Button
